import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded';
import FolderZipRoundedIcon from '@mui/icons-material/FolderZipRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import TerminalRoundedIcon from '@mui/icons-material/TerminalRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { parseImportedConnection } from '@utils/helpers/connectionImport';
import { GENESIS_VIEW_ORIGIN } from '@utils/helpers/viewCorsProtocol';
import type { SavedConnection, SavedConnectionProtocol } from '@/types/connection.types';

interface ConnectionDialogProps {
  open: boolean;
  initialConnection: SavedConnection | null;
  onClose: () => void;
  onSave: (connection: SavedConnection) => void | Promise<void>;
}

type FormState = {
  nickname: string;
  protocol: SavedConnectionProtocol;
  labelsText: string;
  endpoint: string;
  port: string;
  bucket: string;
  region: string;
  prefix: string;
  username: string;
  accessKeyId: string;
  secretAccessKey: string;
  password: string;
  sshPrivateKeyName: string;
  forcePathStyle: boolean;
  // Public file URL + Branding
  publicUrl: string;
  brandLogoUrl: string;
  brandAccentColor: string;
};

const DEFAULT_FORM: FormState = {
  nickname: '',
  protocol: 'r2',
  labelsText: '',
  endpoint: '',
  port: '',
  bucket: '',
  region: 'auto',
  prefix: '',
  username: '',
  accessKeyId: '',
  secretAccessKey: '',
  password: '',
  sshPrivateKeyName: '',
  forcePathStyle: true,
  publicUrl: '',
  brandLogoUrl: '',
  brandAccentColor: '',
};

// NOTE: 'dreamhost-object' (DreamObjects S3) is intentionally omitted from the
// picker — it's unused and its S3/bucket form confused DreamHost web-hosting
// users. The protocol stays valid in the type + backend (VALID_PROTOCOLS) so
// any pre-existing connection still loads, edits, and saves correctly.
const PROTOCOL_OPTIONS: Array<{ value: SavedConnectionProtocol; label: string }> = [
  { value: 'r2', label: 'Cloudflare R2' },
  { value: 's3', label: 'Amazon S3' },
  { value: 'minio', label: 'MinIO / Custom S3' },
  { value: 'sftp', label: 'DreamHost / SFTP' },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(12,12,14,0.92)',
    borderRadius: 2,
    fontFamily: '"Inter", sans-serif',
  },
  '& .MuiInputLabel-root': {
    fontFamily: '"Space Grotesk", sans-serif',
    fontSize: 12,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  '& .MuiFormHelperText-root': {
    fontFamily: '"Space Grotesk", sans-serif',
    fontSize: 10,
    letterSpacing: '0.06em',
  },
} as const;

function toFormState(connection: SavedConnection | null): FormState {
  if (!connection) return DEFAULT_FORM;

  return {
    nickname: connection.nickname,
    protocol: connection.protocol,
    labelsText: (connection.labels || []).join(', '),
    endpoint: connection.endpoint,
    port: connection.port ? String(connection.port) : '',
    bucket: connection.bucket || '',
    region: connection.region || '',
    prefix: connection.prefix || '',
    username: connection.username || '',
    accessKeyId: connection.accessKeyId || '',
    secretAccessKey: connection.secretAccessKey || '',
    password: connection.password || '',
    sshPrivateKeyName: connection.sshPrivateKeyName || '',
    forcePathStyle: !!connection.forcePathStyle,
    publicUrl: connection.publicUrl || '',
    brandLogoUrl: connection.brandLogoUrl || '',
    brandAccentColor: connection.brandAccentColor || '',
  };
}

/**
 * Host segment allowlist: ASCII letters, digits, dots, hyphens, optional :port.
 * Applied to the RAW host segment of the input (before URL parsing) so
 * IDN homographs (Cyrillic 'а' that looks like 'a') and invisible chars
 * (zero-width space) are caught — the URL parser silently punycodes
 * non-ASCII hostnames, hiding the attack.
 */
const SAFE_HOST_SEGMENT_RE = /^[a-z0-9.\-]+(?::\d+)?$/i;

/** Extract the raw host segment between scheme and path. */
function rawHostSegment(input: string): string | null {
  const m = /^https?:\/\/([^/?#]+)/i.exec(input);
  return m ? m[1] : null;
}

/**
 * Shared https-URL validator used by both publicUrl and logoUrl.
 * - Requires https:// scheme
 * - URL constructor must accept it
 * - Hostname must be ASCII alphanumerics + dot + hyphen only
 *   (kills IDN homograph + invisible-char attacks)
 * - Rejects userinfo (no `https://user:pass@evil.com`)
 * - Rejects unsafe chars in raw input (CSS/HTML escape boundary)
 * Returns '' on success, error message on failure. Empty input = ''.
 */
function validateHttpsUrl(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!/^https:\/\//i.test(trimmed)) return `${label} must start with https://`;
  if (/["'<>\\]/.test(trimmed)) return 'URL contains unsafe characters';

  const rawHost = rawHostSegment(trimmed);
  if (!rawHost) return 'Invalid hostname';
  // Reject userinfo BEFORE URL parse — it's part of the raw host segment.
  if (rawHost.includes('@')) return 'URL must not contain credentials';
  if (!SAFE_HOST_SEGMENT_RE.test(rawHost)) {
    return 'Hostname contains characters that may not resolve as expected (use plain ASCII)';
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return 'Invalid URL format';
  }
  if (url.protocol !== 'https:') return 'Only https:// is allowed';
  if (!url.hostname) return 'Invalid hostname';
  return '';
}

/**
 * Validate the publicUrl field per CEO + Eng review specs:
 * - https:// only, URL parse, hostname allowlist, no userinfo
 * - Char allowlist on raw input (CSS/HTML escape boundary)
 * Empty input is allowed (clears the feature).
 */
export function validatePublicUrl(value: string): string {
  return validateHttpsUrl(value, 'Public file base URL');
}

/** Validate logo URL: same rules as publicUrl. Empty allowed. */
export function validateLogoUrl(value: string): string {
  return validateHttpsUrl(value, 'Logo URL');
}

/**
 * Normalize a URL value for storage: trim, strip trailing slashes.
 * Prevents data corruption when downstream code concatenates
 * publicUrl + '/' + key (one tenant with `https://x/` and another
 * with `https://x` would otherwise produce different cache keys).
 * Returns the canonical form, or undefined for empty input.
 */
export function normalizeUrlForStorage(value: string): string | undefined {
  const trimmed = value.trim().replace(/\/+$/, '');
  return trimmed || undefined;
}

/**
 * Validate accent color per Eng review issue 2A:
 * - Strict /^#[0-9a-fA-F]{6}$/
 * - Contrast >= 3.0 against #202124 (DESIGN.md tenant theming spec)
 * Returns error message or empty string if valid.
 * Empty input allowed (uses neutral default).
 */
export function validateAccentColor(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return 'Must be a 6-digit hex color (e.g., #4F9CFF)';
  }
  // Compute relative luminance for the entered color and for #202124 (bg).
  // Per WCAG: contrast = (L1+0.05)/(L2+0.05) where L1 is the lighter color.
  const lum = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const adjust = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    return 0.2126 * adjust(r) + 0.7152 * adjust(g) + 0.0722 * adjust(b);
  };
  const colorLum = lum(trimmed);
  const bgLum = lum('#202124');
  const ratio = (Math.max(colorLum, bgLum) + 0.05) / (Math.min(colorLum, bgLum) + 0.05);
  if (ratio < 3.0) {
    return `Contrast ${ratio.toFixed(1)}:1 too low against the dark viewer background. Pick a brighter color (need ≥3.0:1).`;
  }
  return '';
}

/**
 * Canonicalize an accent color for storage: trim + lowercase.
 * Downstream CSS templates may compare against this value as a string;
 * always-lowercase prevents `#4F9CFF` vs `#4f9cff` mismatch bugs.
 * Returns canonical form, or undefined for empty input.
 */
export function normalizeAccentColor(value: string): string | undefined {
  const trimmed = value.trim().toLowerCase();
  return trimmed || undefined;
}

function omitUndefined<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
}

function buildEndpointPreview(form: FormState): string {
  if (!form.endpoint) return '';
  if (form.protocol === 'sftp') return `sftp://${form.endpoint}${form.port ? `:${form.port}` : ''}`;

  const base = /^https?:\/\//i.test(form.endpoint) ? form.endpoint : `https://${form.endpoint}`;
  return `${base}${form.bucket ? `/${form.bucket}` : ''}${form.prefix ? `/${form.prefix.replace(/^\/+/, '')}` : ''}`;
}

function getEndpointLabel(protocol: SavedConnectionProtocol): string {
  if (protocol === 'sftp') return 'Server URL';
  if (protocol === 'r2') return 'R2 Account Endpoint';
  if (protocol === 'dreamhost-object') return 'DreamObjects Endpoint';
  return 'Endpoint';
}

function getEndpointHelper(protocol: SavedConnectionProtocol): string {
  if (protocol === 'sftp') return 'DreamHost: the SFTP server from your panel (e.g. iad1-shared-xxx.dreamhost.com) or your domain';
  if (protocol === 'r2') return 'Use the R2 S3 API endpoint: https://ACCOUNT_ID.r2.cloudflarestorage.com';
  if (protocol === 'dreamhost-object') return 'Example: https://objects-us-east-1.dream.io';
  return 'Example: https://s3.amazonaws.com';
}

function getPortHelper(protocol: SavedConnectionProtocol): string {
  if (protocol === 'sftp') return 'Default 22 (DreamHost uses 22)';
  return 'Optional';
}

function getBucketHelper(protocol: SavedConnectionProtocol, error?: string): string {
  if (error) return error;
  if (protocol === 'r2') return 'Cloudflare R2 bucket name';
  if (protocol === 'dreamhost-object') return 'DreamObjects bucket name';
  return 'Storage bucket name';
}

function getRegionHelper(protocol: SavedConnectionProtocol): string {
  if (protocol === 'r2') return 'Use auto for Cloudflare R2.';
  if (protocol === 'dreamhost-object') return 'DreamObjects region (e.g. us-east-1)';
  return 'Region or location hint';
}

export function ConnectionDialog({ open, initialConnection, onClose, onSave }: ConnectionDialogProps) {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [showMore, setShowMore] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(toFormState(initialConnection));
    setWarnings([]);
    setFieldErrors({});
    setSaveError(null);
    setSaving(false);
    setShowMore(Boolean(initialConnection?.prefix || initialConnection?.region || initialConnection?.sshPrivateKeyName || initialConnection?.protocol === 'sftp'));
  }, [open, initialConnection]);

  const endpointPreview = useMemo(() => buildEndpointPreview(form), [form]);
  const isSftp = form.protocol === 'sftp';

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setSaveError(null);
    setFieldErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  function handleProtocolChange(protocol: SavedConnectionProtocol) {
    setForm((current) => ({
      ...current,
      protocol,
      region: protocol === 'r2'
        ? (current.region || 'auto')
        : (current.protocol === 'r2' && current.region === 'auto' ? '' : current.region),
      forcePathStyle: protocol === 'r2' ? true : current.forcePathStyle,
    }));
    setSaveError(null);
    setFieldErrors({});
    if (protocol === 'sftp') setShowMore(true);
  }

  function validate(): boolean {
    const nextErrors: Record<string, string> = {};

    if (!form.nickname.trim()) nextErrors.nickname = 'Nickname is required.';
    if (!form.endpoint.trim()) nextErrors.endpoint = 'Server or endpoint is required.';

    if (isSftp) {
      if (!form.username.trim()) nextErrors.username = 'Username is required for SFTP.';
      if (!initialConnection && !form.password.trim() && !form.sshPrivateKeyName.trim()) {
        nextErrors.password = 'Enter a password or SSH key reference.';
      }
    } else {
      if (!form.bucket.trim()) nextErrors.bucket = 'Bucket is required.';
      if (!initialConnection && !form.accessKeyId.trim()) nextErrors.accessKeyId = 'Access key ID is required.';
      if (!initialConnection && !form.secretAccessKey.trim()) nextErrors.secretAccessKey = 'Secret key is required.';

      // Public file URL + branding (S3-family only; SFTP doesn't expose public URLs)
      const publicUrlErr = validatePublicUrl(form.publicUrl);
      if (publicUrlErr) nextErrors.publicUrl = publicUrlErr;
      const logoErr = validateLogoUrl(form.brandLogoUrl);
      if (logoErr) nextErrors.brandLogoUrl = logoErr;
      const accentErr = validateAccentColor(form.brandAccentColor);
      if (accentErr) nextErrors.brandAccentColor = accentErr;
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function handleImportClick() {
    importInputRef.current?.click();
  }

  async function handleImportChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    const raw = await file.text();
    const result = parseImportedConnection(raw);
    setWarnings(result.warnings);

    const imported = result.connection;
    if (!imported) return;

    setForm((current) => ({
      ...current,
      nickname: imported.nickname || current.nickname,
      protocol: imported.protocol || current.protocol,
      labelsText: imported.labels?.join(', ') || current.labelsText,
      endpoint: imported.endpoint || current.endpoint,
      port: imported.port ? String(imported.port) : current.port,
      bucket: imported.bucket || current.bucket,
      region: imported.region || current.region,
      prefix: imported.prefix || current.prefix,
      username: imported.username || current.username,
      accessKeyId: imported.accessKeyId || current.accessKeyId,
      secretAccessKey: imported.secretAccessKey || current.secretAccessKey,
      password: imported.password || current.password,
      sshPrivateKeyName: imported.sshPrivateKeyName || current.sshPrivateKeyName,
      forcePathStyle: imported.forcePathStyle ?? current.forcePathStyle,
    }));

    event.target.value = '';
  }

  async function handleSave() {
    if (!validate()) return;

    const now = new Date().toISOString();
    const connection = omitUndefined({
      id: initialConnection?.id || crypto.randomUUID(),
      nickname: form.nickname.trim(),
      protocol: form.protocol,
      labels: form.labelsText.split(',').map((item) => item.trim()).filter(Boolean),
      endpoint: form.endpoint.trim(),
      port: form.port ? Number(form.port) : undefined,
      bucket: form.bucket.trim() || undefined,
      region: form.region.trim() || undefined,
      prefix: form.prefix.trim() || undefined,
      username: form.username.trim() || undefined,
      accessKeyId: form.accessKeyId.trim() || undefined,
      secretAccessKey: form.secretAccessKey.trim() || undefined,
      password: form.password.trim() || undefined,
      sshPrivateKeyName: form.sshPrivateKeyName.trim() || undefined,
      forcePathStyle: isSftp ? undefined : form.forcePathStyle,
      // Public file URL + branding.
      // Normalize on save so downstream code can trust the canonical form.
      publicUrl: isSftp ? undefined : normalizeUrlForStorage(form.publicUrl),
      brandLogoUrl: isSftp ? undefined : normalizeUrlForStorage(form.brandLogoUrl),
      brandAccentColor: isSftp ? undefined : normalizeAccentColor(form.brandAccentColor),
      createdAt: initialConnection?.createdAt || now,
      updatedAt: now,
    }) as SavedConnection;

    setSaving(true);
    setSaveError(null);
    try {
      await onSave(connection);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        className: 'glass-card',
        sx: {
          background: 'linear-gradient(180deg, rgba(14,14,16,0.98), rgba(20,20,24,0.96))',
          borderRadius: 3,
          border: '1px solid rgba(143,245,255,0.16)',
          boxShadow: '0 0 0 1px rgba(143,245,255,0.08), 0 40px 80px rgba(0,0,0,0.55)',
        },
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
          <Box>
            <Typography className="font-label" sx={{ fontSize: 10, color: '#8ff5ff', mb: 0.75 }}>
              Storage Connection
            </Typography>
            <Typography variant="h4" sx={{ fontSize: { xs: 28, md: 34 } }}>
              {initialConnection ? 'Edit Storage' : 'Add Storage'}
            </Typography>
            <Typography sx={{ color: 'text.secondary', mt: 1 }}>
              {isSftp
                ? 'Connect over SFTP to a web host like DreamHost. Browse, upload, and download work directly.'
                : 'Connect S3-compatible storage (Cloudflare R2, DreamObjects, etc.) with its API endpoint and keys.'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <Chip icon={<CloudQueueRoundedIcon />} label="Server managed" size="small" />
            <Chip icon={<TerminalRoundedIcon />} label={isSftp ? 'SFTP mode' : 'Cloud mode'} size="small" />
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2.5}>
          <input
            ref={importInputRef}
            type="file"
            accept=".json"
            hidden
            onChange={(event) => { void handleImportChange(event); }}
          />

          {warnings.map((warning) => (
            <Alert key={warning} severity="warning" sx={{ borderRadius: 2 }}>
              {warning}
            </Alert>
          ))}

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 1,
              borderRadius: 3,
              bgcolor: 'rgba(11,11,13,0.78)',
              border: '1px solid rgba(143,245,255,0.08)',
            }}
          >
            {PROTOCOL_OPTIONS.map((option) => (
              <Button
                key={option.value}
                onClick={() => handleProtocolChange(option.value)}
                variant={form.protocol === option.value ? 'contained' : 'text'}
                sx={{
                  flex: { xs: '1 1 calc(50% - 4px)', md: '0 0 auto' },
                  bgcolor: form.protocol === option.value ? (option.value === 'sftp' ? '#ff51fa' : '#8ff5ff') : 'transparent',
                  color: form.protocol === option.value ? (option.value === 'sftp' ? '#250020' : '#003f43') : 'text.secondary',
                  '&:hover': {
                    bgcolor: form.protocol === option.value ? undefined : 'rgba(255,255,255,0.04)',
                  },
                }}
              >
                <Typography className="font-label" sx={{ fontSize: 10 }}>
                  {option.label}
                </Typography>
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(143,245,255,0.1)',
              bgcolor: 'rgba(9,11,13,0.84)',
              p: 2,
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Box>
                <Typography className="font-label" sx={{ fontSize: 10, color: '#8ff5ff' }}>
                  Endpoint Preview
                </Typography>
                <Typography sx={{ mt: 1, wordBreak: 'break-all' }}>
                  {endpointPreview || 'Awaiting connection details...'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Chip icon={<FolderZipRoundedIcon />} label={isSftp ? 'SFTP file access' : 'S3-compatible API'} size="small" />
                {!isSftp && <Chip icon={<KeyRoundedIcon />} label="Live credentials" size="small" />}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <TextField
              label="Nickname"
              value={form.nickname}
              onChange={(event) => update('nickname', event.target.value)}
              error={!!fieldErrors.nickname}
              helperText={fieldErrors.nickname || 'Display name for this storage connection'}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="Labels"
              value={form.labelsText}
              onChange={(event) => update('labelsText', event.target.value)}
              helperText="Comma-separated labels"
              fullWidth
              sx={fieldSx}
            />
          </Box>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' } }}>
            <TextField
              label={getEndpointLabel(form.protocol)}
              value={form.endpoint}
              onChange={(event) => update('endpoint', event.target.value)}
              error={!!fieldErrors.endpoint}
              helperText={fieldErrors.endpoint || getEndpointHelper(form.protocol)}
              fullWidth
              sx={fieldSx}
            />
            <TextField
              label="Port"
              value={form.port}
              onChange={(event) => update('port', event.target.value)}
              placeholder={isSftp ? '22' : undefined}
              helperText={getPortHelper(form.protocol)}
              fullWidth
              sx={fieldSx}
            />
          </Box>

          {isSftp ? (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              <TextField
                label="Username"
                value={form.username}
                onChange={(event) => update('username', event.target.value)}
                error={!!fieldErrors.username}
                helperText={fieldErrors.username || 'Hosting account username only; do not include @domain.'}
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="Password"
                type="password"
                value={form.password}
                onChange={(event) => update('password', event.target.value)}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || 'SFTP password, or set an SSH key under More Options.'}
                fullWidth
                sx={fieldSx}
              />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <TextField
                  label="Bucket"
                  value={form.bucket}
                  onChange={(event) => update('bucket', event.target.value)}
                  error={!!fieldErrors.bucket}
                  helperText={getBucketHelper(form.protocol, fieldErrors.bucket)}
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="Region"
                  value={form.region}
                  onChange={(event) => update('region', event.target.value)}
                  helperText={getRegionHelper(form.protocol)}
                  fullWidth
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <TextField
                  label="Access Key ID"
                  value={form.accessKeyId}
                  onChange={(event) => update('accessKeyId', event.target.value)}
                  error={!!fieldErrors.accessKeyId}
                  helperText={fieldErrors.accessKeyId}
                  fullWidth
                  sx={fieldSx}
                />
                <TextField
                  label="Secret Access Key"
                  type="password"
                  value={form.secretAccessKey}
                  onChange={(event) => update('secretAccessKey', event.target.value)}
                  error={!!fieldErrors.secretAccessKey}
                  helperText={fieldErrors.secretAccessKey}
                  fullWidth
                  sx={fieldSx}
                />
              </Box>
            </>
          )}

          {/* Public file URL + Branding section (S3-family only). */}
          {!isSftp && (
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: 'rgba(11,11,13,0.78)',
                border: '1px solid rgba(143,245,255,0.08)',
              }}
            >
              <Typography className="font-label" sx={{ fontSize: 10, color: '#8ff5ff', mb: 1.5, letterSpacing: '0.18em' }}>
                Public Viewer Domain + Branding
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Public File Base URL (optional)"
                  value={form.publicUrl}
                  onChange={(event) => update('publicUrl', event.target.value)}
                  placeholder="https://seedfiles.genesisframeworks.com"
                  helperText={
                    fieldErrors.publicUrl || (
                      <>
                        Optional. Use only after files allow CORS from {GENESIS_VIEW_ORIGIN}.{' '}
                        <Link
                          component={RouterLink}
                          to="/admin/setup-guide"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: '#8ff5ff', textDecorationColor: 'rgba(143,245,255,0.4)' }}
                        >
                          Open View CORS protocol ↗
                        </Link>
                      </>
                    )
                  }
                  error={!!fieldErrors.publicUrl}
                  fullWidth
                  sx={fieldSx}
                  inputProps={{ 'aria-label': 'Public file base URL' }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Logo URL (optional)"
                    value={form.brandLogoUrl}
                    onChange={(event) => update('brandLogoUrl', event.target.value)}
                    placeholder="https://drive.opendoorpc.org/_brand/logo.svg"
                    helperText={
                      fieldErrors.brandLogoUrl ||
                      'Top-left of the viewer. PNG/SVG over HTTPS. Max 160×24px.'
                    }
                    error={!!fieldErrors.brandLogoUrl}
                    fullWidth
                    sx={fieldSx}
                  />
                  <TextField
                    label="Accent Color (optional)"
                    value={form.brandAccentColor}
                    onChange={(event) => update('brandAccentColor', event.target.value)}
                    placeholder="#4F9CFF"
                    helperText={
                      fieldErrors.brandAccentColor ||
                      'Active mode buttons. Hex only. Auto-validated for ≥3.0:1 contrast.'
                    }
                    error={!!fieldErrors.brandAccentColor}
                    fullWidth
                    sx={fieldSx}
                  />
                </Stack>
              </Stack>
            </Box>
          )}

          <Button onClick={() => setShowMore((current) => !current)} sx={{ alignSelf: 'flex-start', px: 0 }}>
            <Typography className="font-label" sx={{ fontSize: 10 }}>
              {showMore ? 'Hide Advanced Options' : 'More Options'}
            </Typography>
          </Button>

          <Collapse in={showMore}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: 'rgba(11,11,13,0.78)',
                border: '1px solid rgba(143,245,255,0.08)',
              }}
            >
              <Stack spacing={2}>
                {isSftp ? (
                  <>
                    <TextField
                      label="Remote Path"
                      value={form.prefix}
                      onChange={(event) => update('prefix', event.target.value)}
                      placeholder="/home/username/yourdomain.com"
                      helperText="Folder to start browsing from. DreamHost: /home/USER/yourdomain.com"
                      fullWidth
                      sx={fieldSx}
                    />
                    <TextField
                      label="SSH Private Key"
                      value={form.sshPrivateKeyName}
                      onChange={(event) => update('sshPrivateKeyName', event.target.value)}
                      helperText="Store a key reference or note for this connection"
                      fullWidth
                      sx={fieldSx}
                    />
                  </>
                ) : (
                  <>
                    <TextField
                      label="Prefix"
                      value={form.prefix}
                      onChange={(event) => update('prefix', event.target.value)}
                      helperText="Optional folder or namespace inside the bucket"
                      fullWidth
                      sx={fieldSx}
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={form.forcePathStyle}
                          onChange={(event) => update('forcePathStyle', event.target.checked)}
                        />
                      )}
                      label={(
                        <Typography className="font-label" sx={{ fontSize: 10 }}>
                          Force path-style URLs
                        </Typography>
                      )}
                    />
                  </>
                )}
              </Stack>
            </Box>
          </Collapse>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        {saveError && (
          <Alert severity="error" sx={{ borderRadius: 2, width: '100%', flexBasis: '100%' }}>
            {saveError}
          </Alert>
        )}
        <Button startIcon={<UploadFileRoundedIcon />} onClick={handleImportClick}>
          Import JSON
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { void handleSave(); }}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              bgcolor: isSftp ? '#ff51fa' : '#8ff5ff',
              color: isSftp ? '#250020' : '#003f43',
              '&:hover': { bgcolor: isSftp ? '#ff6ffb' : '#69edf9' },
            }}
          >
            {saving ? 'Saving...' : 'Save Connection'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
